import {
  createFileRoute,
  Outlet,
  retainSearchParams,
  useChildMatches,
} from '@tanstack/react-router';
import {Box} from '@chakra-ui/react';
import {useQuery, useQueryClient} from '@tanstack/react-query';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {loadNoiseProject} from './crew.noise';
import {NoiseToolbar} from '../components/noise/NoiseToolbar';
import {
  resolveProjectSelection,
  cropProjectSelection,
  sameSelection,
  midpointOf,
  setSelectionCurrent,
  visibleProjectWindow,
  type ProjectSelection,
} from '../components/noise/projectSelection';
import {
  projectSearchFor,
  projectSearchSelection,
  sameProjectSearch,
  validateProjectSearch,
} from '../components/noise/projectSearch';
import {useNowAfterMount} from '../components/noise/context';
import {
  assignmentsAt,
  locationLines,
  orderLocations,
  createPlayheadSignal,
  PlayheadLevelsContext,
  PlayheadSignalContext,
  ProjectViewContext,
  type ProjectViewCtx,
} from '../components/noise/projectView';
import {ProjectTimeline} from '../components/noise/ProjectTimeline';
import {usePlayheadPinKey} from '../components/noise/playheadPin';
import {locationTags} from '../components/noise/rangeTags';
import {PlayheadDisplay} from '../components/noise/PlayheadDisplay';
import {
  LevelPicker,
  LevelSelect,
  useLevelPick,
} from '../components/noise/LevelPicker';
import {SERIES_STORE} from '../components/noise/seriesSelection';
import {useProjectLogs} from '../components/noise/useProjectLogs';
import {
  NativeSelectField,
  NativeSelectRoot,
} from '../components/chakra-snippets/native-select';
import {noiseQueryKeys} from '../components/noise/queries';
import {spanWithinDay, useLatest} from '../components/noise/chartUtils';
import {timePresets, type TimePreset} from '../components/noise/timePresets';
import {seo} from '../utils/seo';

// One view at a time: the map is only useful at a size worth giving the whole viewport
// to, and the cards below it were pushing it into a letterbox. Map and cards are
// sibling routes so a view is linkable and survives a reload, and so each one's markup
// lives in its own file; the shared toolbars, timeline and data stay here (see
// projectView.ts for what crosses the seam).
const MAP_ROUTE = '/crew/noise/project/$projectId/map';
const LIST_ROUTE = '/crew/noise/project/$projectId/list';

// Two choices over two routes, which is the whole of it: how wide the cards are is not a
// third view but a way of laying them out, and it is picked where the rest of the list's
// layout is — the toolbar at its foot, on the page it applies to, and remembered per browser
// rather than put on the URL (see listColumns.ts). This switcher used to carry it as a
// "Raster" entry, which put half of one view's layout in the header of both.
//
// The charts first, because that is where a project opens (see the index route) and a
// dropdown that led with something else would read as if the landing view were an
// exception. The map is a view you switch to.
const VIEWS = [
  {value: 'list', label: 'Graph', to: LIST_ROUTE},
  {value: 'map', label: 'Map', to: MAP_ROUTE},
] as const satisfies ReadonlyArray<{
  value: string;
  label: string;
  to: string;
}>;

type View = (typeof VIEWS)[number]['value'];

// How long the timeline has to be still before the URL is brought up to date. Long enough
// that a drag or a hover across a chart is one write rather than a hundred, short enough
// that letting go and reaching for the address bar finds it already there.
const URL_SETTLE_MS = 300;

// `project/` is a static segment because $device already occupies the dynamic slot
// under /crew/noise — a cuid and a device name are indistinguishable at
// match time, so the two routes would be ambiguous. This also leaves every
// bookmarked device URL untouched.
export const Route = createFileRoute('/crew/noise/project/$projectId')({
  // Live, and the moment being looked at while it is off — see projectSearch.ts. On the
  // layout and not on either view, because the timeline is the layout's: both views are
  // read at the same instant, and switching between them must not change it.
  //
  // No loaderDeps, deliberately: none of this fetches anything. The project's stored
  // history is one query the browser then reads locally (see useProjectLogs), so pinning
  // a moment is not a reason to go back to the server.
  validateSearch: validateProjectSearch,
  // And they stay on the URL through every navigation inside this subtree that doesn't
  // mention them: switching view, and the index route's redirect into the list. Stated
  // once here, where the params are declared, rather than as a `search` to remember at
  // each link and redirect — the next one added would be the one that forgot.
  search: {middlewares: [retainSearchParams(['live', 'from', 'to'])]},
  loader: ({params}) => loadNoiseProject({data: {projectId: params.projectId}}),
  // Layout route: opts out of pending UI so its shell survives child navigation.
  // See the same line on `/_main` and `/crew`.
  pendingMs: Infinity,
  head: ({loaderData}) =>
    seo({title: `Noise – ${loaderData?.name ?? 'Project'}`}),
  component: NoiseProjectDetail,
});

function NoiseProjectDetail() {
  const {projectId} = Route.useParams();
  const initial = Route.useLoaderData();
  const navigate = Route.useNavigate();
  const queryClient = useQueryClient();
  const search = Route.useSearch();
  // Live is the default: this page's job during a festival is watching the monitors
  // right now, which is also what it did before there was a cursor. A URL that names a
  // moment is a page someone pinned and sent, so it opens on that instead.
  //
  // For a festival that is over — or hasn't started — live is an empty page, so it opens
  // on the whole event with the playhead in its middle instead (see resolveProjectSelection,
  // which is what a live-less page with no crop resolves to). That rule lived on the index
  // route's links, which meant it held for exactly one way in: a bookmark, a pasted URL,
  // the index route's own redirect and every future link — a pin on the map, a device's
  // place chip — all opened a finished event live. It is a fact about the project, so it
  // belongs on the page that has the project.
  const [live, setLive] = useState(search.live !== false);
  // How you're looking at the project. Component state, because none of it fetches
  // anything — the whole event is in the browser — and because a gesture on the timeline
  // commits per animation frame, which is more often than an address bar can be written
  // (see the sync below). The URL is the record of it, not the source.
  //
  // What the user has picked of the timeline, or null while they have picked nothing —
  // see resolveProjectSelection for why that null carries weight. The crop comes from the
  // URL on the first render rather than from an effect after it, so a pinned link's own
  // window is what the first paint draws and hydration sees the same page the server
  // sent; the playhead inside it is the page's alone (see projectSearch.ts).
  const [chosen, setChosen] = useState<ProjectSelection | null>(() =>
    projectSearchSelection(search, null),
  );

  // The URL trails the page, and it trails it late. A crop drag commits once per animation
  // frame (see ProjectTimeline's useFrameCommit), so navigating per commit would
  // re-render every match sixty times a second — and spend Safari's history allowance, a
  // hundred entries per thirty seconds, in about two of them. So the pick lands in state
  // at once and in the URL once the gesture has been still for a moment. Nothing on the
  // page reads it back, so the delay is invisible; what it costs is a moment before the
  // URL is worth copying.
  //
  // That settling is what makes the history entry below worth having: one step per
  // gesture, rather than one per frame of it. Moving the playhead writes nothing at all —
  // it isn't in the URL — so hovering the charts neither navigates nor pushes.
  useEffect(() => {
    const next = projectSearchFor(live, chosen);
    if (sameProjectSearch(next, search)) return;
    const timer = setTimeout(
      () =>
        // Only the search changes, and `unsafeRelative` is what says so: with no `to`, a
        // navigation resolves against the route it was made from — this layout — and would
        // take the URL up out of the child view on screen, which the index route then
        // redirects back to the list. So touching the timeline on the map would bounce out
        // of the map. Against the current pathname instead, whatever it is.
        //
        // Over the search that is there rather than instead of it: `next` names every param
        // this page owns — including the ones it is clearing (see projectSearchFor) — and
        // says nothing about anybody else's.
        navigate({
          unsafeRelative: 'path',
          search: (prev) => ({...prev, ...next}),
          // A step of its own, not a replace: everything that reaches here is a decision
          // about what is being looked at — the mode, or a window someone framed — and
          // backing out of one is what a back button is for. Back walks out of live mode
          // and out through the crops that were picked; the adopt effect below is what
          // puts each of them on screen as it does.
          resetScroll: false,
        }),
      URL_SETTLE_MS,
    );
    return () => clearTimeout(timer);
    // The crop's two ends and not `chosen` itself: the playhead inside it moves once per
    // animation frame while a chart is hovered, and it is not in the URL — so depending on
    // the object would restart the settle sixty times a second and a long hover would
    // leave a crop that has already been let go of unwritten. The search is listed by its
    // three values for the same reason the adopt effect below is: this has to re-decide
    // when the URL moves under it, and not because a render handed it a new object.
  }, [
    live,
    chosen?.start,
    chosen?.end,
    search.live,
    search.from,
    search.to,
    navigate,
  ]);

  // And the other direction: the back button, or a link followed into a tab that is
  // already on this page. The URL changed without this page having written it, so the
  // page takes it — and equal values are dropped, since the page's own write arrives back
  // here as a change like any other and would otherwise hand every consumer a new
  // selection object for a selection that hasn't moved.
  useEffect(() => {
    setLive(search.live !== false);
    // The crop is only read back while the URL is showing one. Live carries no range by
    // construction, and the pick it leaves behind is deliberately kept in state so that
    // switching back returns to it — reading an absent range as "nothing picked" would
    // throw exactly that away.
    if (search.live === false) {
      setChosen((prev) => {
        // Around the playhead already standing there, which is why this is computed in the
        // updater rather than beside it: the URL says nothing about the instant, so the
        // one to keep is whatever the page holds at the moment it takes the crop.
        const next = projectSearchSelection(search, prev);
        return sameSelection(prev, next) ? prev : next;
      });
    }
    // The three values rather than the object: this must run when the URL moves, and not
    // because a render handed it a new identity for the same one.
  }, [search.live, search.from, search.to]);

  const {data: project} = useQuery({
    queryKey: noiseQueryKeys.project(projectId),
    queryFn: () => loadNoiseProject({data: {projectId}}),
    initialData: initial,
  });

  // Which view is on screen comes from the match, not from state — the URL is the one
  // place it's recorded. Anything that isn't the cards is the map; the index route is
  // neither, and redirects to the list before this is ever asked. The column count is
  // deliberately not read here: it is the list's own layout, and a switcher that changed
  // when it did would be a control for two different things.
  const shown = useChildMatches({
    select: (matches): View =>
      matches.some((m) => m.routeId === LIST_ROUTE) ? 'list' : 'map',
  });

  // `picked` is every series the charts draw — weighting included, there being no page-wide
  // weighting any more. It travels through the context, because everything on the view below
  // must answer it the same way, and the single-number readouts take their one series off it
  // where they need one (see primarySeries). Not in the URL: it is how you read a chart, not
  // what you are looking at — it is remembered instead, per browser (see seriesSelection.ts).
  //
  // Per view and not per project page, which is why it is read down here where the view is
  // known: the two ask different things of a pick. The cards draw every line picked, each
  // series in its own shade, so a set is what the list is for; a pin is a badge with room for
  // one number, so the map takes one and stores one — and a set carried over from the list
  // would have drawn its first and dropped the rest without saying so. Held by the layout all
  // the same, since the control that sets it is in the layout's own header, and switching
  // view simply re-reads the other view's remembered pick (see useLevelPick).
  const mapOnly = shown === 'map';
  const {picked, toggleSeries} = useLevelPick({
    store: mapOnly ? SERIES_STORE.map : SERIES_STORE.list,
    single: mapOnly,
  });

  // The map's create tool (see ProjectViewCtx's `placing`). Dropped the moment the page is
  // not on the map, during the render that leaves it — so the view arrived at never paints
  // with the tool on, and coming back to the map finds it unarmed rather than still waiting
  // for a click from a visit ago. React's own pattern for adjusting state to a prop.
  const [placing, setPlacing] = useState(false);
  if (placing && !mapOnly) setPlacing(false);

  // Without a Maps key there is no map to switch to, so the list is all there is.
  const mapAvailable = project.apiKey != null;

  // Null for the first paint (see useNowAfterMount), so SSR falls back to the
  // project's own end and the clamp appears immediately after mount. Re-ticks
  // once a minute, so the edge keeps up during a running event.
  const now = useNowAfterMount();
  const pickable = visibleProjectWindow(project, now ?? project.end);

  // The other half of the opening decision above, and it has to be an effect for the same
  // reason `now` is null to begin with: the server has no business guessing which side of
  // the clock the reader is on, so the first paint opens live — as it always did — and the
  // clock corrects it once there is one.
  //
  // Once, ever. `now` re-ticks every minute and the guard is what keeps this an opening
  // decision rather than a rule: a festival that ends while someone is watching it live
  // must not throw them out of live mode as the clock passes its last minute.
  //
  // It runs after the adopt effect above has taken the URL, which is the order that makes
  // the two agree: an explicit `?live=` settles the question by itself and is claimed here
  // before this can look at a clock at all.
  const opened = useRef(search.live != null);
  useEffect(() => {
    if (opened.current || now == null) return;
    opened.current = true;
    if (now < project.start || now > project.end) setLive(false);
  }, [now, project.start, project.end]);
  // Memoized on the window's ends rather than on `pickable` itself, which is a fresh
  // object every render: the timeline's props, `range` and selectionRef are all derived
  // from this, and a new identity per render would put a new one in front of each.
  const selection = useMemo(
    () => resolveProjectSelection(chosen, pickable),
    [chosen, pickable.start, pickable.end],
  );

  // Which instant the page is looking at: an instant while scrubbing, and null while
  // live, meaning "whatever is standing there now". Decided here rather than in each
  // view, so the map and the list can't answer it differently.
  const viewedAt = live ? null : selection.current;

  // The crop alone, pinned on its two ends: it is handed to every card through the
  // context, and a fresh object per render would be a new context value on every frame
  // of a hover — which is the one thing the split below exists to prevent.
  const range = useMemo(
    () => ({start: selection.start, end: selection.end}),
    [selection.start, selection.end],
  );

  // And the strip the crop lives in, pinned the same way and for the same reason:
  // `pickable` is a fresh object every render, and this one goes through the context to
  // every card as the limit a pinch or a two-finger drag may reach. Its right edge moves
  // once a minute on a running festival, which is a new value then and only then.
  const bounds = useMemo(
    () => ({start: pickable.start, end: pickable.end}),
    [pickable.start, pickable.end],
  );

  // Which monitors stood at each location at the instant being viewed, resolved once
  // for the whole page rather than per card and again in the map.
  //
  // The key is what makes a scrub free: hovering a trace re-resolves this on every
  // frame and, for all but the handful that cross an assignment boundary, gets back
  // exactly the same rows. Keyed on the rows' ids and not on their devices, because two
  // abutting assignments of one monitor are two different rows — and one of them is
  // closed, which is what decides whether the ⋮ offers to end it.
  //
  // Sorted before any of that, and here rather than in each view: the cards, the roster
  // of chips under them and the pins on the map are three renderings of one set, and
  // there is exactly one place that decides its order (see compareLocations). Not left
  // to the query either — Postgres sorts `Bühne 10` before `Bühne 2`.
  const ordered = useMemo(
    () => orderLocations(project.locations),
    [project.locations],
  );
  // The same places carrying the stretches crew tagged to be ignored at each, for the crop's
  // Leq to leave out (see locationEnergyIndex). Markers set nothing aside, so only ranges.
  //
  // Keyed on the ignored ranges alone, so adding or editing a comment — which sets nothing
  // aside — leaves these, and every energy index summed off them, as they were.
  const ignoreKey = project.tags
    .filter((t) => t.type === 'IGNORE' && t.end != null)
    .map((t) => `${t.deviceId}|${t.locationId}|${t.start}|${t.end}`)
    .join(' ');
  const withIgnored = useMemo(
    () =>
      ordered.map((location) => ({
        ...location,
        ignored: project.tags.flatMap((t) =>
          t.type === 'IGNORE' &&
          t.end != null &&
          (t.deviceId != null ||
            t.locationId == null ||
            t.locationId === location.id)
            ? [{deviceId: t.deviceId, start: t.start, end: t.end}]
            : [],
        ),
      })),
    [ordered, ignoreKey],
  );
  const locationsKey = ordered
    .map(
      (l) =>
        `${l.id}:${assignmentsAt(l.assignments, viewedAt)
          .map((a) => a.id)
          .join(',')}`,
    )
    .join('|');
  // `ordered` is a dependency as well as the key, and has to be: a refetch after an
  // assignment change hands back an equal-but-new graph carrying the same ids, so a memo
  // watching the key alone would go on serving the previous render's rows, with the
  // lastSeen and the open/closed flag they had before the change that caused the
  // refetch. viewedAt is deliberately absent — the key already says when it matters.
  const locations = useMemo(
    () =>
      ordered.map((location) => ({
        location,
        assignments: assignmentsAt(location.assignments, viewedAt),
      })),
    [locationsKey, ordered],
  );

  // Read by scrubTo so it can start from the selection in effect without depending on
  // it: hovering a row chart fires once per animation frame, and a callback that
  // changed identity with the playhead would put a new context value — and so a fresh
  // chart prop — in front of every consumer on every one of those frames.
  const selectionRef = useLatest(selection);
  // Same reason: the window's right edge follows the clock on a running festival, and
  // the bound setter below must not change identity when it ticks.
  const pickableRef = useLatest(pickable);

  // Hovering a row chart is the same commit dragging the playhead makes, so it goes
  // to the same place: an override, which is also what pins the crop from following
  // the live edge any further. An unchanged instant keeps whatever was there before —
  // that's most frames of a slow hover, and it also means merely passing the pointer
  // over the playhead where it already stands doesn't pin an untouched timeline.
  //
  // Which is the whole of what the equality below is for now that the mark stays where it is
  // put: nothing reports a pointer leaving any more (see the context's `scrubTo`), so the
  // frames this drops are the repeats a slow hover makes within one minute.
  const scrubTo = useCallback(
    (at: number) => {
      setChosen((prev) => {
        const from = selectionRef.current;
        const next = setSelectionCurrent(from, at);
        return next.current === from.current ? prev : next;
      });
    },
    [selectionRef],
  );

  // The playhead menu's windows (see timePresets): the last hour, today, the event's days —
  // none until the clock is known, since which of them exist is a question about now.
  // Recomputed as the clock ticks over a minute, which is how "today" keeps its live edge.
  const presets = useMemo(
    () => (now == null ? [] : timePresets(project, now)),
    [project.start, project.end, now],
  );
  // Which of them the crop is on, if any — so its row is ticked. Only while scrubbing, live
  // being a row of its own.
  const activePreset = live
    ? null
    : (presets.find(
        (p) => p.start === selection.start && p.end === selection.end,
      ) ?? null);
  const goLive = useCallback(() => setLive(true), []);
  // Leaving live onto a window is a crop like any other — the same state a drag on the strip
  // commits, so the URL and the back button treat it alike. A window that runs to the live
  // edge is stored running to the project's end instead, which the clamp to what is pickable
  // turns back into "until now" on every tick (see resolveProjectSelection): today goes on
  // being today, rather than freezing at the minute it was picked.
  const pickPreset = useCallback(
    (preset: TimePreset) => {
      setLive(false);
      setChosen({
        start: preset.start,
        end: preset.end >= pickableRef.current.end ? project.end : preset.end,
        current: midpointOf(preset),
      });
    },
    [pickableRef, project.end],
  );

  // Cropping from a row chart: the in/out keys hand over one end, a drag across the
  // trace both. One commit either way — exact, never snapped, and a single state
  // update, so a drag can't briefly put a start past an end.
  const cropTo = useCallback(
    (crop: {start?: number; end?: number}) => {
      setChosen((prev) =>
        cropProjectSelection(
          crop,
          prev ?? selectionRef.current,
          pickableRef.current,
        ),
      );
    },
    [selectionRef, pickableRef],
  );

  // One request for the project's whole stored history, and then nothing: every
  // number below is read out of it locally, so the timeline and both pickers cost
  // no round trip. Skipped entirely while live.
  // Which places the list has on it, as it last reported them (see ProjectViewCtx's
  // `setListed`). The list owns the choice — it is remembered per browser, beside the
  // cards it arranges — and the layout only needs it for the timeline, which is drawn up
  // here and speaks for what is on screen. Null until the list has read its store, and
  // then the strip speaks for every place rather than for none for a frame.
  const [listed, setListed] = useState<ReadonlySet<string> | null>(null);

  // What the timeline speaks for: every place on the map, and on the list only the ones
  // it is showing — both its coverage and its limits (see useProjectLogs).
  const timeline = useMemo(() => {
    const all = mapOnly || listed == null || listed.size === 0;
    return {
      all,
      // Carrying what is ignored at each, so the strip's coverage and breaches leave it
      // out the same way the Leq does.
      locations: all
        ? withIgnored
        : withIgnored.filter((l) => listed.has(l.id)),
    };
  }, [mapOnly, listed, withIgnored]);

  // Every comment in the project, the source of both lists below.
  const comments = useMemo(
    () => project.tags.filter((t) => t.type === 'COMMENT'),
    [project.tags],
  );

  // The comments the strip marks: those reaching the places it speaks for, by the rule
  // the cards' charts use (see locationTags), so the strip marks no note the cards don't
  // show. With their text, which the playhead's pill names while it is in one. Memoized,
  // because the markers layer is memoized on its props.
  const timelineComments = useMemo(() => {
    const reaching = timeline.all
      ? comments
      : [
          ...new Map(
            timeline.locations
              .flatMap((l) =>
                locationTags(comments, l.id, locationLines(l.assignments)),
              )
              .map((t) => [t.id, t]),
          ).values(),
        ];
    return reaching.map(({start, end, text}) => ({start, end, text}));
  }, [comments, timeline]);

  const {levels, rangeIndex, traces, gaps, breaches, isFetching} =
    useProjectLogs({
      projectId,
      live,
      picked,
      selection,
      // The raw locations with their whole assignment history, not the playhead-resolved
      // `locations` below: a range's Leq is summed over every minute a monitor stood at a
      // place, which has nothing to do with the instant being viewed. Everything it
      // returns is keyed by location id, so the order is immaterial — it takes the sorted
      // array only so there is one of them on the page.
      locations: withIgnored,
      timeline,
    });

  // Assigning or ending changes both this project's locations and which devices
  // are still available, on this page and on the index.
  const refresh = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: noiseQueryKeys.project(projectId),
      }),
      queryClient.invalidateQueries({
        queryKey: noiseQueryKeys.assignableDevices,
      }),
      queryClient.invalidateQueries({queryKey: noiseQueryKeys.projects}),
    ]);
  }, [queryClient, projectId]);

  const view = useMemo<ProjectViewCtx>(
    () => ({
      project,
      live,
      picked,
      range,
      bounds,
      locations,
      scrubTo,
      cropTo,
      // Absent while live, an instant having no range to average.
      rangeIndex,
      traces,
      refresh,
      setListed,
      placing,
      setPlacing,
    }),
    [
      project,
      live,
      picked,
      range,
      bounds,
      locations,
      scrubTo,
      cropTo,
      rangeIndex,
      traces,
      refresh,
      placing,
    ],
  );

  // One signal for the layout's lifetime, so the subscription every row chart holds
  // never has to be torn down and re-established. Pushed from an effect rather than
  // during render: the charts move a line to a pixel, and the scale that pixel is
  // measured against is set by their own effects, which run first.
  // Created holding the first instant, so nothing subscribing to it sees a frame of "no
  // playhead" before the effect below has run.
  const [playheadSignal] = useState(() => createPlayheadSignal(viewedAt));
  useEffect(() => playheadSignal.set(viewedAt), [playheadSignal, viewedAt]);
  // Space pins the playhead where it stands, or lets it go — wherever the timeline is up to
  // move it (see belowOpen below).
  usePlayheadPinKey(!live && !placing);

  return (
    <ProjectViewContext.Provider value={view}>
      {/* The signal outside the readings, because it never changes and they do —
          see PlayheadSignalContext. */}
      <PlayheadSignalContext.Provider value={playheadSignal.subscribe}>
        <PlayheadLevelsContext.Provider value={levels}>
          {/* Grows past the viewport rather than being clamped to it — `1 0 auto`, not
              `1`. A sticky child can only stick within its own parent, so a page box
              exactly one viewport tall would let the toolbars scroll away the moment a
              long list ran past it. At least the viewport (so the map has a full one to
              fill), and as tall as the list when there is more. */}
          <Box display="flex" flexDirection="column" flex="1 0 auto">
            <NoiseToolbar
              // The clock in the middle of the strip, and leading it from the left on a
              // phone, where the arrow gives way to the controls (see centerTitle).
              centerTitle
              title={
                // The instant being read, and the menu that decides whether that instant is
                // now (see PlayheadDisplay). At every width, since it is how the page is
                // switched out of live.
                <PlayheadDisplay
                  name={project.name}
                  withDate={!spanWithinDay(pickable.end - pickable.start)}
                  live={live}
                  presets={presets}
                  activePreset={activePreset}
                  onLive={goLive}
                  onPreset={pickPreset}
                  pending={isFetching}
                />
              }
              /* A second toolbar, and only while scrubbing. Live mode reads what is
                 arriving now — there is no instant to point at and no crop to pick, so
                 the strip had nothing left to do but take up the room the map wants.
                 The selection it edits is not lost with it: the layout owns it, so
                 switching back returns to the crop and the instant you left.

                 Folded while the map is being edited, too: the strip moves the playhead,
                 and the playhead holds still then (see `placing`). Passed either way and
                 folded by the flag, so either animates the strip shut rather than dropping
                 it (see NoiseToolbar). */
              belowOpen={!live && !placing}
              below={
                <ProjectTimeline
                  window={pickable}
                  selection={selection}
                  // The crop belongs to the charts: they are drawn over it and their Leqs
                  // averaged across it. The map has none — a pin reads what stood there at
                  // the playhead, off the whole payload rather than the crop — so the two
                  // grips there would pick a window nothing on screen answers to, over the
                  // one thing the map does want the strip for. The playhead stays in both.
                  //
                  // Nothing is lost switching to the map: the selection is this layout's,
                  // so the crop is still where it was left when the charts come back.
                  croppable={shown === 'list'}
                  // A prop rather than a field on the context below: the strip is the
                  // only thing that draws these, and the context is read by every card
                  // on the page (see ProjectViewCtx) — a field there would re-render all
                  // of them the moment the payload landed.
                  gaps={gaps}
                  breaches={breaches}
                  comments={timelineComments}
                  // Straight into state: what the timeline hands back is exactly the
                  // override to remember, and pinning it is what stops an untouched
                  // crop from following the live edge any further. Identical values
                  // are dropped — every gesture snaps to the quarter hour, so
                  // consecutive frames of a drag often commit the same selection, and
                  // re-rendering the page for it would be pure waste.
                  onCommit={(next) =>
                    setChosen((prev) =>
                      sameSelection(prev, next) ? prev : next,
                    )
                  }
                />
              }
            >
              {/* A set on the list, one of nine on the map (see the pick above). The crop's
                  Leq is offered on the list only: the map has no crop. */}
              {mapOnly ? (
                <LevelSelect
                  live={live}
                  picked={picked}
                  onPick={toggleSeries}
                />
              ) : (
                <LevelPicker
                  live={live}
                  picked={picked}
                  onToggleSeries={toggleSeries}
                />
              )}
              {mapAvailable && (
                <NativeSelectRoot size="xs" w="auto">
                  <NativeSelectField
                    aria-label="View"
                    value={shown}
                    // The timeline and everything else page-wide survive the switch
                    // because the layout owns them and stays mounted — only the route
                    // travels. Not a replace: the view is part of the URL, and back should
                    // return to the one you came from.
                    //
                    // No search of its own, and neither view has one: the moment being looked
                    // at is the layout's and rides along on its retainSearchParams, and how
                    // the cards are laid out is remembered rather than travelling in the URL
                    // (see listColumns.ts) — so a trip to the map and back finds the list
                    // arranged the way it was left.
                    onChange={(e) => {
                      const view =
                        VIEWS.find((v) => v.value === e.target.value) ??
                        VIEWS[0];
                      navigate({
                        to: view.to,
                        params: {projectId},
                        search: {},
                      });
                    }}
                    items={VIEWS.map(({value, label}) => ({value, label}))}
                  />
                </NativeSelectRoot>
              )}
            </NoiseToolbar>

            {/* Everything the toolbars left over, edge to edge: the map fills it, and
                the list sets its own gutter. No padding and no frame here — a border
                around the map would draw a box inside a box, and the one thing this
                page is for is seeing the site. */}
            <Box flex="1" minH="0" display="flex" flexDirection="column">
              <Outlet />
            </Box>
          </Box>
        </PlayheadLevelsContext.Provider>
      </PlayheadSignalContext.Provider>
    </ProjectViewContext.Provider>
  );
}
